// src/components/organization/subscription/PackageCard.js
// Component for displaying subscription package cards
import React, { useState } from "react";

const PackageCard = ({ pkg, isPopular = false, onSelectPackage }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const formatFeatureValue = (key, value) => {
    if (value === -1) return "Unlimited";
    if (value === 0) return "Not included";

    // Add specific formatting based on feature type
    const timeBasedFeatures = {
      stuff: "lifetime",
      players: "lifetime",
      teams: "lifetime",
      events: "per month",
      matches: "per month",
      leagues: "per year",
      competitions: "per year",
    };

    const formattedValue = value.toLocaleString();
    const timeUnit = timeBasedFeatures[key];

    return timeUnit ? `${formattedValue} ${timeUnit}` : formattedValue;
  };

  const getFeatureIcon = (featureKey) => {
    const icons = {
      stuff: "👥",
      players: "🎮",
      teams: "🏆",
      events: "📅",
      matches: "⚽",
      leagues: "🏟️",
      competitions: "🏅",
    };
    return icons[featureKey] || "✓";
  };

  const getFeatureLabel = (featureKey) => {
    const labels = {
      stuff: "Staff Members",
      players: "Players",
      teams: "Teams",
      events: "Events",
      matches: "Matches",
      leagues: "Leagues",
      competitions: "Create Competitions",
    };
    return labels[featureKey] || featureKey;
  };

  const features = Object.entries(pkg.features);

  // Handle payment process
  const handlePayment = async () => {
    try {
      setIsProcessing(true);
      setPaymentError(null);

      console.log(`💳 Initiating payment for package: ${pkg.name} (${pkg.id})`);

      // Call backend payment endpoint
      const response = await fetch(
        `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/organization/subscription/pay`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            packageId: pkg.id,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Payment failed");
      }

      console.log(`✅ Payment response:`, data);

      // Handle different payment response types
      if (data.success) {
        if (data.data.type === "mock") {
          // Mock payment - subscription activated immediately
          console.log(`🧪 Mock payment completed - subscription activated`);

          // Show success message
          alert(
            `🎉 Subscription activated successfully!\n\nPackage: ${pkg.name}\nMode: Development (Mock Payment)`
          );

          // Refresh page or redirect to dashboard
          if (onSelectPackage) {
            onSelectPackage(pkg, data);
          } else {
            window.location.reload();
          }
        } else if (data.data.type === "geidea") {
          // Real payment - redirect to payment link
          console.log(
            `🔗 Redirecting to payment link: ${data.data.paymentLink}`
          );

          // Store transaction info for later reference
          localStorage.setItem(
            "pendingTransaction",
            JSON.stringify({
              transactionId: data.data.transaction.transactionId,
              packageId: pkg.id,
              packageName: pkg.name,
              amount: data.data.transaction.amount,
              paymentLink: data.data.paymentLink,
            })
          );

          // Redirect to payment page
          window.open(data.data.paymentLink, "_blank");

          // Show instructions to user
          alert(
            `💳 Payment link opened in new tab.\n\nPackage: ${pkg.name}\nAmount: ${data.data.transaction.amount} SAR\n\nPlease complete the payment and return to this page.`
          );
          
          // Reload page after user closes the alert
          window.location.reload();

          if (onSelectPackage) {
            onSelectPackage(pkg, data);
          }
        }
      } else {
        throw new Error(data.message || "Payment initialization failed");
      }
    } catch (error) {
      console.error("💥 Payment error:", error);
      setPaymentError(error.message);

      // Show user-friendly error message
      alert(
        `❌ Payment Error\n\n${error.message}\n\nPlease try again or contact support.`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isPopular ? "ring-4 ring-orange-100" : "border-gray-200"
      }`}
      style={{
        borderColor: isPopular ? "#DE561F" : undefined,
        "--tw-ring-color": isPopular ? "#DE561F20" : undefined,
      }}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span
            className="text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg"
            style={{
              background: "linear-gradient(to right, #DE561F, #B8441A)",
            }}
          >
            Most Popular
          </span>
        </div>
      )}

      <div className="p-8">
        {/* Package Header */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
          <p className="text-gray-600 mb-4">{pkg.description}</p>

          {/* Price */}
          <div className="mb-4">
            <span className="text-5xl font-bold text-gray-900">
              {pkg.price}
            </span>
            <span className="text-gray-500 ml-2">SAR/ year</span>
          </div>

          {/* Duration */}
          <div className="text-sm text-gray-500">
            {pkg.duration} days subscription
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-4 mb-8">
          <h4 className="font-semibold text-gray-900 text-lg border-b border-gray-200 pb-2">
            What's Included
          </h4>
          {features.map(([key, value]) => (
            <div key={key} className="flex items-start justify-between py-2">
              <div className="flex items-start">
                <span className="mr-3 text-lg mt-0.5">
                  {getFeatureIcon(key)}
                </span>
                <span className="text-gray-700 leading-tight">
                  {getFeatureLabel(key)}
                </span>
              </div>
              <span
                className={`font-semibold text-right ml-4 ${
                  value === 0
                    ? "text-gray-400"
                    : value === -1
                    ? "text-green-600"
                    : ""
                }`}
                style={{
                  color: value !== 0 && value !== -1 ? "#DE561F" : undefined,
                }}
              >
                {formatFeatureValue(key, value)}
              </span>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {paymentError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm font-medium">
              ❌ {paymentError}
            </p>
          </div>
        )}

        {/* CTA Button */}
        <button
          className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 text-white shadow-lg hover:shadow-xl ${
            !isPopular ? "bg-gray-900 hover:bg-gray-800" : ""
          } ${isProcessing ? "opacity-75 cursor-not-allowed" : ""}`}
          style={{
            background: isPopular
              ? "linear-gradient(to right, #DE561F, #B8441A)"
              : undefined,
            backgroundHover: isPopular
              ? "linear-gradient(to right, #C54E1C, #A03D17)"
              : undefined,
          }}
          onMouseEnter={(e) => {
            if (isPopular && !isProcessing) {
              e.target.style.background =
                "linear-gradient(to right, #C54E1C, #A03D17)";
            }
          }}
          onMouseLeave={(e) => {
            if (isPopular && !isProcessing) {
              e.target.style.background =
                "linear-gradient(to right, #DE561F, #B8441A)";
            }
          }}
          onClick={handlePayment}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing...
            </div>
          ) : (
            `Choose ${pkg.name}`
          )}
        </button>

        {/* Payment Info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            🔒 Secure payment powered by Geidea
          </p>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
